import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TalentService } from '../talent.service';
import { TestAssignmentService } from '../test-assignment.service';
import { PdfOptionsDialogComponent } from './pdf-options-dialog.component';

@Component({
    selector: 'app-test-assignment-detail',
    templateUrl: './test-assignment-detail.component.html',
    styleUrls: ['./test-assignment-detail.component.css'],
    standalone: false
})
export class TestAssignmentDetailComponent implements OnInit {
  assignmentId: number;
  assignment: any = null;
  test: any = null;
  evaluationData: any = null;
  
  isLoading: boolean = false;
  isDownloading: boolean = false;
  isRerunning: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';

  // LLM integrity analysis
  integrityAnalysis: any = null;
  isAnalyzing: boolean = false;
  analysisError: string = '';

  expandedQuestions: Set<number> = new Set();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private talentService: TalentService,
    private testAssignmentService: TestAssignmentService
  ) {}

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.assignmentId = +params['id'];
      this.loadAssignmentDetail();
    });
  }

  loadAssignmentDetail() {
    this.isLoading = true;
    this.errorMessage = '';

    this.talentService.getTestAssignmentDetail(this.assignmentId).subscribe(
      (assignment) => {
        this.assignment = assignment;
        console.log(this.assignment)
        
        // Parse evaluation reasoning if exists
        if (this.assignment.evaluation_reasoning) {
          try {
            this.evaluationData = JSON.parse(this.assignment.evaluation_reasoning);
            console.log('Parsed evaluation data:', this.evaluationData);
          } catch (e) {
            console.error('Error parsing evaluation_reasoning:', e);
          }
        }
        
        this.loadTestDetail();
        this.loadIntegrityAnalysis();
      },
      (error) => {
        console.error('Error loading assignment:', error);
        this.errorMessage = 'Failed to load assignment details';
        this.isLoading = false;
      }
    );
  }

  // Load a previously-cached LLM verdict (no cost). Does nothing if none exists.
  loadIntegrityAnalysis() {
    if (!this.assignmentId) return;
    this.talentService.getIntegrityAnalysis(this.assignmentId).subscribe(
      (res) => { this.integrityAnalysis = res && res.analysis ? res.analysis : null; },
      (error) => { console.error('Error loading integrity analysis:', error); }
    );
  }

  // Run the analysis on demand. Caches the verdict server-side.
  runIntegrityAnalysis() {
    if (!this.assignmentId || this.isAnalyzing) return;
    this.isAnalyzing = true;
    this.analysisError = '';
    this.talentService.runIntegrityAnalysis(this.assignmentId).subscribe(
      (res) => {
        this.isAnalyzing = false;
        if (res && res.analysis) {
          this.integrityAnalysis = res.analysis;
        } else {
          this.analysisError = (res && res.error) ? res.error : 'Analysis returned no result.';
        }
      },
      (error) => {
        this.isAnalyzing = false;
        console.error('Error running integrity analysis:', error);
        this.analysisError = 'Analysis failed. Check that the API and model key are configured.';
      }
    );
  }

  // ---- Tier display helpers ----

  tierLabel(tier: string): string {
    switch (tier) {
      case 'clear': return 'Clear';
      case 'review': return 'Review';
      case 'suspicious': return 'Suspicious';
      default: return 'Unknown';
    }
  }

  tierBadgeClass(tier: string): string {
    switch (tier) {
      case 'clear': return 'badge-success';
      case 'review': return 'badge-warning';
      case 'suspicious': return 'badge-danger';
      default: return 'badge-secondary';
    }
  }

  severityClass(severity: string): string {
    switch (severity) {
      case 'high': return 'badge-danger';
      case 'medium': return 'badge-warning';
      case 'low': return 'badge-secondary';
      default: return 'badge-secondary';
    }
  }

  // Glanceable 4-bucket label derived from the 0-100 risk score.
  // Thresholds are easy to tune later without touching the prompt.
  // Buckets nest inside the LLM tier bands (clear 0-24 / review 25-59 /
  // suspicious 60-100) so the header bucket and the detail tier never disagree.
  riskBucketLabel(score: number): string {
    if (score == null) return 'Unknown';
    if (score < 15) return 'Very safe';
    if (score < 25) return 'Likely safe';
    if (score < 60) return 'Unsure';
    return 'Suspicious';
  }

  riskBucketClass(score: number): string {
    if (score == null) return 'badge-secondary';
    if (score < 15) return 'badge-success';
    if (score < 25) return 'badge-success';
    if (score < 60) return 'badge-warning';
    return 'badge-danger';
  }

  loadTestDetail() {
    this.talentService.getCandidateTestDetail(this.assignment.test_id).subscribe(
      (test) => {
        this.test = test;
        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading test:', error);
        this.errorMessage = 'Failed to load test details';
        this.isLoading = false;
      }
    );
  }

  toggleQuestion(index: number) {
    if (this.expandedQuestions.has(index)) {
      this.expandedQuestions.delete(index);
    } else {
      this.expandedQuestions.add(index);
    }
  }

  isQuestionExpanded(index: number): boolean {
    return this.expandedQuestions.has(index);
  }

  getTestLink(): string {
    return this.testAssignmentService.getTestLink(this.assignment.unique_code);
  }

  copyTestLink(): void {
    this.testAssignmentService.copyTestLink(this.assignment.unique_code);
  }

  getStatusBadgeClass(status: string): string {
    return this.testAssignmentService.getStatusBadgeClass(status);
  }

  getStatusDisplay(status: string): string {
    return this.testAssignmentService.getStatusDisplay(status);
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  }

  viewTest() {
    this.router.navigate(['/ai-test-detail', this.assignment.test_id]);
  }

  viewCandidate() {
    this.router.navigate(['/candidate-detail', this.assignment.candidate_id]);
  }

  goBack() {
    this.router.navigate(['/test-assignments']);
  }

  downloadPdf() {
    if (!this.assignment || this.assignment.status !== 'completed' || !this.test) {
      return;
    }

    // Show options dialog
    const dialogRef = this.dialog.open(PdfOptionsDialogComponent, {
      width: '800px',
      data: { questions: this.test.questions }
    });

    dialogRef.afterClosed().subscribe(options => {
      if (!options) {
        return; // User cancelled
      }

      this.isDownloading = true;
      
      this.talentService.downloadAssignmentPdf(this.assignmentId, options).subscribe(
        (blob) => {
          // Create a download link
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `assessment_${this.assignmentId}_${this.assignment.candidate_name.replace(/\s+/g, '_')}.pdf`;
          link.click();
          
          // Clean up
          window.URL.revokeObjectURL(url);
          this.isDownloading = false;
        },
        (error) => {
          console.error('Error downloading PDF:', error);
          this.errorMessage = 'Failed to generate PDF report';
          this.isDownloading = false;
        }
      );
    });
  }

  getOptionLetter(index: number): string {
    return String.fromCharCode(65 + index);
  }

  getCandidateAnswer(questionIndex: number): string | null {
    if (!this.assignment.responses) {
      return null;
    }
    // Responses are stored as direct strings indexed by question number
    const response = this.assignment.responses[questionIndex.toString()];
    return response || null;
  }

  isAnswerCorrect(question: any, questionIndex: number): boolean {
    const answer = this.getCandidateAnswer(questionIndex);
    if (!answer || question.type !== 'multiple_choice') {
      return false;
    }
    
    // Try direct comparison first
    if (answer === question.correct_answer) {
      return true;
    }
    
    // If correct_answer is a letter (A, B, C, D), get the corresponding option text
    if (question.options && question.correct_answer && question.correct_answer.length === 1) {
      const correctIndex = question.correct_answer.charCodeAt(0) - 65; // A=0, B=1, etc
      if (correctIndex >= 0 && correctIndex < question.options.length) {
        return answer === question.options[correctIndex];
      }
    }
    
    // If answer is a letter, get the corresponding option text
    if (answer.length === 1 && question.options) {
      const answerIndex = answer.charCodeAt(0) - 65;
      if (answerIndex >= 0 && answerIndex < question.options.length) {
        return question.options[answerIndex] === question.correct_answer;
      }
    }
    
    return false;
  }

  getQuestionEvaluation(questionIndex: number): any {
    if (!this.evaluationData || !this.evaluationData.question_evaluations) {
      return null;
    }
    return this.evaluationData.question_evaluations.find(
      (evaluation: any) => evaluation.question_index === questionIndex
    );
  }

  getQuestionScore(questionIndex: number): number | null {
    const evaluation = this.getQuestionEvaluation(questionIndex);
    return evaluation ? evaluation.score : null;
  }

  getQuestionReasoning(questionIndex: number): string | null {
    const evaluation = this.getQuestionEvaluation(questionIndex);
    return evaluation ? evaluation.reasoning : null;
  }

  isCandidateOption(questionIndex: number, option: string, optionIndex: number): boolean {
    const answer = this.getCandidateAnswer(questionIndex);
    if (!answer) {
      return false;
    }
    
    // Check if answer matches option text directly
    if (answer === option) {
      return true;
    }
    
    // Check if answer is a letter (A, B, C, D) matching this option's index
    if (answer.length === 1) {
      const answerLetter = answer.toUpperCase();
      const expectedLetter = this.getOptionLetter(optionIndex);
      return answerLetter === expectedLetter;
    }
    
    return false;
  }

  getDifficultyDisplay(difficulty: string): string {
    if (!difficulty) return 'N/A';
    return difficulty.charAt(0).toUpperCase() + difficulty.slice(1);
  }

  getDifficultyBadgeClass(difficulty: string): string {
    switch(difficulty) {
      case 'basic': return 'badge-success';
      case 'intermediate': return 'badge-warning';
      case 'advanced': return 'badge-danger';
      default: return 'badge-secondary';
    }
  }

  // ---- Integrity signals (light-touch anti-cheating) ----

  getIntegrity(): any {
    return this.assignment && this.assignment.integrity_data ? this.assignment.integrity_data : null;
  }

  hasIntegrityData(): boolean {
    return !!this.getIntegrity();
  }

  getTabSwitches(): number {
    const integrity = this.getIntegrity();
    return integrity && integrity.tab_switches ? integrity.tab_switches : 0;
  }

  getTimeAwayMs(): number {
    const integrity = this.getIntegrity();
    return integrity && integrity.time_away_ms ? integrity.time_away_ms : 0;
  }

  getCopyCount(): number {
    const integrity = this.getIntegrity();
    return integrity && integrity.copy_count ? integrity.copy_count : 0;
  }

  getPasteEvents(): any[] {
    const integrity = this.getIntegrity();
    return integrity && integrity.paste_events ? integrity.paste_events : [];
  }

  getCopyEvents(): any[] {
    const integrity = this.getIntegrity();
    return integrity && integrity.copy_events ? integrity.copy_events : [];
  }

  getSwitchEvents(): any[] {
    const integrity = this.getIntegrity();
    return integrity && integrity.switch_events ? integrity.switch_events : [];
  }

  getServerPasteFlags(): any[] {
    const integrity = this.getIntegrity();
    return integrity && integrity.server_paste_flags ? integrity.server_paste_flags : [];
  }

  // Flags that match a preceding in-app copy = the candidate duplicating their
  // own code. Not evidence of external cheating.
  getInternalPasteFlags(): any[] {
    return this.getServerPasteFlags().filter(f => this.flagIsInternal(f));
  }

  // Flags with no matching in-app copy = potentially pasted from outside. These
  // are the only genuine cheating signal among the paste data.
  getExternalPasteFlags(): any[] {
    return this.getServerPasteFlags().filter(f => !this.flagIsInternal(f));
  }

  getExternalPasteEventCount(): number {
    return this.getClassifiedPasteEvents().filter(p => !p.internal).length;
  }

  // A raw browser paste event is only worth highlighting if it's sizeable.
  // Small pastes (a word, a line) are normal editing noise, not a signal.
  private readonly PASTE_SIGNIFICANT_LEN = 40;

  isSignificantPaste(evt: any): boolean {
    return (evt?.len || 0) >= this.PASTE_SIGNIFICANT_LEN;
  }

  // Label shown on a raw paste row. We only make a positive claim ("self-copy")
  // when we can confirm it matched an in-app copy. We never label a raw paste
  // "external" – that verdict lives with the server paste flags, which are the
  // only reliable external signal.
  pasteRowLabel(evt: any): 'self-copy' | 'large' | '' {
    if (evt?.internal) return 'self-copy';
    if (this.isSignificantPaste(evt)) return 'large';
    return '';
  }

  // ---- Internal-vs-external paste classification (client-side) ----
  // The server annotates new data in merge_integrity_data, but we recompute here
  // so already-completed assignments (stored before that logic existed) are also
  // classified correctly. A paste is "internal" (self-copy of own code) when its
  // text matches a copy captured within the assessment shortly before it.

  private _pasteClassCache: { key: any; pastes: any[] } | null = null;
  private readonly INTERNAL_COPY_WINDOW_S = 60;

  getClassifiedPasteEvents(): any[] {
    const integrity = this.getIntegrity();
    if (this._pasteClassCache && this._pasteClassCache.key === integrity) {
      return this._pasteClassCache.pastes;
    }
    const copies = this.getCopyEvents();
    const pastes = this.getPasteEvents().map(p => ({
      ...p,
      internal: typeof p.internal === 'boolean' ? p.internal : this.isInternalPaste(p, copies)
    }));
    this._pasteClassCache = { key: integrity, pastes };
    return pastes;
  }

  private isInternalPaste(paste: any, copies: any[]): boolean {
    const ptext = (paste?.text || '').trim();
    if (!ptext) return false;
    const pt = Date.parse(paste?.t);
    for (const c of copies) {
      const ctext = (c?.text || '').trim();
      if (!ctext || ctext !== ptext) continue;
      const ct = Date.parse(c?.t);
      if (isNaN(pt) || isNaN(ct)) return true;
      const deltaS = (pt - ct) / 1000;
      if (deltaS >= -2 && deltaS <= this.INTERNAL_COPY_WINDOW_S) return true;
    }
    return false;
  }

  private flagIsInternal(flag: any): boolean {
    if (flag && typeof flag.internal === 'boolean') return flag.internal;
    const q = String(flag?.q);
    const delta = Math.abs(flag?.delta ?? flag?.unexplained ?? 0);
    if (delta <= 0) return false;
    const tolerance = Math.max(60, 0.2 * delta);
    let bestDiff: number | null = null;
    let bestInternal = false;
    for (const p of this.getClassifiedPasteEvents()) {
      if (String(p.q) !== q) continue;
      const diff = Math.abs((p.len || 0) - delta);
      if (diff <= tolerance && (bestDiff === null || diff < bestDiff)) {
        bestDiff = diff;
        bestInternal = !!p.internal;
      }
    }
    return bestInternal;
  }

  formatTimeAway(ms: number): string {
    if (!ms || ms <= 0) return '0s';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }

  formatEventTime(t: string): string {
    if (!t) return '';
    const date = new Date(t);
    if (isNaN(date.getTime())) return t;
    return date.toLocaleTimeString();
  }

  hasIntegrityFlags(): boolean {
    return this.getTabSwitches() > 0
      || this.getTimeAwayMs() > 0
      || this.getCopyCount() > 0
      || this.getPasteEvents().length > 0
      || this.getSwitchEvents().length > 0
      || this.getServerPasteFlags().length > 0;
  }

  // ---- Raw event detail view (expandable) ----

  integrityExpanded: boolean = false;
  showRawEvents: boolean = false;
  showPasteEvents: boolean = false;
  expandedPastes: Set<number> = new Set();

  toggleIntegrity(): void {
    this.integrityExpanded = !this.integrityExpanded;
  }

  toggleRawEvents(): void {
    this.showRawEvents = !this.showRawEvents;
  }

  togglePasteEvents(): void {
    this.showPasteEvents = !this.showPasteEvents;
  }

  togglePaste(index: number): void {
    if (this.expandedPastes.has(index)) {
      this.expandedPastes.delete(index);
    } else {
      this.expandedPastes.add(index);
    }
  }

  isPasteExpanded(index: number): boolean {
    return this.expandedPastes.has(index);
  }

  rerunEvaluation() {
    if (!this.assignment || this.assignment.status !== 'completed') {
      return;
    }

    if (!confirm('Are you sure you want to re-run the evaluation? This will overwrite the current score and evaluation.')) {
      return;
    }

    this.isRerunning = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.talentService.rerunEvaluation(this.assignmentId).subscribe(
      (response) => {
        this.isRerunning = false;
        if (response.status === 'success') {
          this.successMessage = 'Evaluation job enqueued successfully. Refresh the page in a few moments to see the updated results.';
          console.log('Re-evaluation job enqueued:', response);
        } else {
          this.errorMessage = response.message || 'Failed to enqueue evaluation job';
        }
      },
      (error) => {
        console.error('Error re-running evaluation:', error);
        this.errorMessage = 'Failed to re-run evaluation';
        this.isRerunning = false;
      }
    );
  }

  cancelAssignment() {
    if (!this.assignment) {
      return;
    }

    if (this.assignment.status === 'completed') {
      this.errorMessage = 'Cannot cancel a completed assessment';
      return;
    }

    if (this.assignment.status === 'cancelled') {
      this.errorMessage = 'This assessment is already cancelled';
      return;
    }

    if (!confirm('Are you sure you want to cancel this assessment? The candidate will no longer be able to access it.')) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.talentService.cancelTestAssignment(this.assignmentId).subscribe(
      (response) => {
        this.isLoading = false;
        if (response.status === 'success') {
          this.successMessage = 'Assessment cancelled successfully';
          this.loadAssignmentDetail();
        } else {
          this.errorMessage = response.message || 'Failed to cancel assessment';
        }
      },
      (error) => {
        console.error('Error cancelling assessment:', error);
        this.errorMessage = 'Failed to cancel assessment';
        this.isLoading = false;
      }
    );
  }
}
